/**
 * MONSTER TOWN — THE LAMP POSTS COME ON. TIME-OF-DAY CREW (src/time/**).
 *
 * A bulb that changes colour is not a light. What reads as "the street lights
 * are on" from a fixed iso camera is the POOL OF LIGHT ON THE GROUND under each
 * one, and there is no such thing in this game until after dark.
 *
 * HOW IT FINDS THE LAMPS WITHOUT IMPORTING THE WORLD CREW
 * Crews never import each other, and there is no bus question for "where are
 * the lamp posts". There is, however, a fact about the art: every bulb in the
 * game is drawn with `flat('sunWarm')`, and `flat()` is cached by colour, so
 * every bulb in a scene ends up merged into exactly ONE mesh sharing exactly one
 * material instance. `src/world/terrain.ts` already relies on this — it finds
 * that mesh by material identity to switch the street on when a kid taps a lamp.
 *
 * So we do the same thing, from the outside: walk the scene graph (core), test
 * `mesh.material === flat('sunWarm')` (the shared art kit), and read the vertex
 * positions out of the merged geometry. Vertices within a couple of units of
 * each other are one bulb. That gives the lamp positions from the art itself,
 * with no cross-crew import and nothing to keep in sync — move a lamp post in
 * `layout.ts` and its pool of light moves with it, because the pool is derived
 * from where the bulb actually IS.
 *
 * WHAT IT COSTS: ONE DRAW CALL, and only after dark.
 * Every pool in a scene is one merged geometry with one material, hidden
 * outright while `night` is 0. The soft edge is VERTEX COLOUR, not a texture and
 * not an alpha ramp: under additive blending a black vertex adds nothing at all,
 * so a disc that goes warm-gold in the middle to black at the rim IS a soft
 * radial glow, for no texture, no shader and no overdraw beyond the disc.
 *
 * ============ ROUND 2: WHAT WAS ACTUALLY WRONG WITH THE FIRST ONE ============
 *
 * (a) NOT ONE BULB WAS LIT. The nimbus at the lamp head was a HORIZONTAL disc of
 *     radius 0.5 sitting at the bulb's own height — and `world/props.ts` puts a
 *     `roofRed` cone of radius 0.44 directly on top of the bulb, overhanging it
 *     on every side. From a camera 45 degrees above, a half-metre horizontal
 *     disc under a half-metre cap is invisible, at every zoom. So every lamp in
 *     the town was a bright pool of light with a dark maroon mushroom over it,
 *     which is the one thing a kid's eye reads as wrong.
 *     THE FIX: the head glow is a BLOB, not a disc — three soft discs in the
 *     three orthogonal planes, so from any of the camera's four rotations there
 *     is a round glow around the lamp head that spills out past the shade. It
 *     needs no camera vector and nothing to rebuild when the view turns, which
 *     a billboard would.
 *
 * (b) THE POOLS WERE HARD-EDGED DECALS. The old fan was ONE triangle ring:
 *     bright centre vertex, black rim vertices, LINEARLY interpolated across.
 *     A linear ramp has a constant gradient that stops dead at the rim, which
 *     the eye reads as an edge (a Mach band) — measured on the captures as
 *     "four overlapping cream ellipses, each with a crisp visible boundary".
 *     THE FIX: four concentric rings with a bell falloff, (1-u^2)^2, whose
 *     derivative is zero at BOTH the middle and the rim. There is no edge left
 *     to see. The peak is lower and the radius wider for the same reason.
 *
 * (c) THE NIGHT GRADE WAS MULTIPLYING THE LAMPLIGHT DOWN. A pool of light is a
 *     LIGHT. It now draws at renderOrder 9995, after the multiply pane (9990)
 *     and before the additive lift (9998), so the night cools the town around a
 *     lamp and not the lamp itself.
 */

import * as THREE from 'three'
import { world } from '../core/scene'
import type { SceneId } from '../core/types'
import { hex } from '../core/palette'
import { flat } from '../render/kit'
import { blendHex } from './look'

/** How far apart two vertices have to be to belong to different bulbs. */
const CLUSTER_RADIUS = 1.8
/** The pool on the pavement. Wide enough to read, tight enough to be a lamp. */
const POOL_RADIUS = 3.5
const POOL_SEGMENTS = 18
/** Concentric rings. More than one is what kills the hard edge — see (b) above. */
const POOL_RINGS = 4
/**
 * How bright the middle of a pool is. LOW, deliberately: additive blending
 * saturates fast, two pools overlap on a pavement all the time, and cream is
 * what spilled milk looks like. This adds warmth first and brightness second.
 */
const POOL_STRENGTH = 0.44
/**
 * Clear of every layer of GROUND. The world crew stacks its road, kerb, crossing
 * and plaza slabs a few hundredths apart; the first pass put the pools at 0.05
 * and half of them came out UNDERNEATH the pavement they were supposed to be
 * lighting, which is exactly what the captures showed.
 */
const POOL_Y = 0.16
/**
 * The glow around the lamp head. Big enough to spill out past the shade, and
 * NO BIGGER — the first version at 1.25/0.3 did light the bulb (measured, and
 * visible in every capture) but three planes at 0.3 add to ~0.9 of pure amber
 * at the core, which saturates to WHITE. Four lamps by a doorway came out as
 * four overlapping cotton balls. A lamp is a small warm point with a halo, so:
 * tighter radius, and a core that lands warm rather than blown.
 */
const HEAD_RADIUS = 1
const HEAD_SEGMENTS = 12
const HEAD_RINGS = 3
/** Per-plane. Three planes cross in the middle, so the core reads at ~3x this. */
const HEAD_STRENGTH = 0.17
/** How far up the bulb's own height the blob is centred — level with the shade. */
const HEAD_LIFT = 0.12
const MAX_VERTICES = 8000

/**
 * AMBER, not white. Additive blending saturates fast: a pale glow colour turns
 * every pool into a white plate on the pavement (the first captures) instead of
 * lamplight. Keeping it close to `uiCoin` means the pool adds WARMTH first and
 * brightness second, which is what a sodium lamp on a grey road looks like.
 */
const GLOW = blendHex(hex('uiCoin'), hex('sunWarm'), 0.15)

/**
 * The falloff. Zero derivative at BOTH ends, so there is no gradient step
 * anywhere for an eye to catch — not in the middle, and crucially not at the
 * rim, which is where the old single-ring fan put a visible circle.
 */
function falloff(u: number): number {
  const k = 1 - u * u
  return k * k
}

interface Rig {
  readonly mesh: THREE.Mesh
  readonly material: THREE.MeshBasicMaterial
  /** Where the BULBS are, in the rig's own space. Diagnostics only. */
  readonly heads: ReadonlyArray<{ x: number; y: number; z: number }>
}

const rigs: Rig[] = []
let bulbCount = 0

function clusters(geo: THREE.BufferGeometry): Array<{ x: number; y: number; z: number }> {
  const attr = geo.getAttribute('position')
  if (attr === undefined) return []
  const n = Math.min(attr.count, MAX_VERTICES)
  const out: Array<{ x: number; y: number; z: number; n: number; top: number }> = []
  for (let i = 0; i < n; i++) {
    const x = attr.getX(i)
    const y = attr.getY(i)
    const z = attr.getZ(i)
    let hit = false
    for (const c of out) {
      if (Math.hypot(c.x / c.n - x, c.z / c.n - z) < CLUSTER_RADIUS) {
        c.x += x
        c.y += y
        c.z += z
        c.n++
        if (y > c.top) c.top = y
        hit = true
        break
      }
    }
    if (!hit) out.push({ x, y, z, n: 1, top: y })
  }
  return out.map((c) => ({ x: c.x / c.n, y: c.top, z: c.z / c.n }))
}

/** Which two axes a disc lies in. `xz` is the pool on the ground. */
type Plane = 'xz' | 'xy' | 'zy'

/**
 * A soft radial disc, built as concentric rings so the falloff can be a curve
 * rather than a straight line. Black at the rim, which under additive blending
 * adds exactly nothing — the disc has no edge, in geometry or in light.
 */
function pushDisc(
  pos: number[],
  col: number[],
  idx: number[],
  cx: number,
  cy: number,
  cz: number,
  radius: number,
  strength: number,
  rings: number,
  segments: number,
  plane: Plane,
): void {
  const base = pos.length / 3
  const r = ((GLOW >> 16) & 255) / 255
  const g = ((GLOW >> 8) & 255) / 255
  const b = (GLOW & 255) / 255
  const put = (dx: number, dy: number, s: number): void => {
    if (plane === 'xz') pos.push(cx + dx, cy, cz + dy)
    else if (plane === 'xy') pos.push(cx + dx, cy + dy, cz)
    else pos.push(cx, cy + dy, cz + dx)
    col.push(r * s, g * s, b * s)
  }
  put(0, 0, strength)
  for (let ring = 1; ring <= rings; ring++) {
    const u = ring / rings
    const rad = radius * u
    const s = strength * falloff(u)
    for (let i = 0; i < segments; i++) {
      const a = (i / segments) * Math.PI * 2
      put(Math.cos(a) * rad, Math.sin(a) * rad, s)
    }
  }
  const ringStart = (n: number): number => base + 1 + (n - 1) * segments
  for (let i = 0; i < segments; i++) {
    idx.push(base, ringStart(1) + i, ringStart(1) + ((i + 1) % segments))
  }
  for (let ring = 1; ring < rings; ring++) {
    const inner = ringStart(ring)
    const outer = ringStart(ring + 1)
    for (let i = 0; i < segments; i++) {
      const j = (i + 1) % segments
      idx.push(inner + i, outer + i, inner + j)
      idx.push(inner + j, outer + i, outer + j)
    }
  }
}

function buildRigFor(bulbMesh: THREE.Mesh): void {
  const spots = clusters(bulbMesh.geometry)
  if (spots.length === 0) return
  bulbCount += spots.length
  const pos: number[] = []
  const col: number[] = []
  const idx: number[] = []
  for (const s of spots) {
    pushDisc(pos, col, idx, s.x, POOL_Y, s.z, POOL_RADIUS, POOL_STRENGTH, POOL_RINGS, POOL_SEGMENTS, 'xz')
    /**
     * THE HEAD. Three soft discs crossing at the bulb, one per plane — a blob,
     * not a plate. The old horizontal disc lived UNDER the lamp's own shade and
     * was never once visible in a capture; this one spills out past it from
     * every camera rotation, and needs no billboarding to do it.
     */
    const hy = s.y + HEAD_LIFT
    for (const plane of ['xz', 'xy', 'zy'] as const) {
      pushDisc(pos, col, idx, s.x, hy, s.z, HEAD_RADIUS, HEAD_STRENGTH, HEAD_RINGS, HEAD_SEGMENTS, plane)
    }
  }
  const geo = new THREE.BufferGeometry()
  geo.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3))
  geo.setAttribute('color', new THREE.Float32BufferAttribute(col, 3))
  geo.setIndex(idx)
  const material = new THREE.MeshBasicMaterial({
    vertexColors: true,
    transparent: true,
    opacity: 0,
    depthWrite: false,
    blending: THREE.AdditiveBlending,
    fog: false,
    toneMapped: false,
    /**
     * DOUBLE-SIDED, and this was a real bug worth the line.
     *
     * The fan sweeps +X toward +Z, which from a camera ABOVE it is clockwise —
     * i.e. every pool of light was back-facing and culled, and the first night
     * captures showed lamp posts with no light under them at all while
     * `__time.lamps()` cheerfully reported eleven visible pools at full opacity.
     * A flat horizontal quad has no meaningful "back", so this is the fix rather
     * than reversing a winding somebody will get wrong again.
     */
    side: THREE.DoubleSide,
  })
  const mesh = new THREE.Mesh(geo, material)
  mesh.name = 'time:lampGlow'
  /**
   * A POOL OF LIGHT IS A LIGHT, so the night grade must not multiply it down.
   * Between the multiply pane (9990) and the additive lift (9998). Depth
   * testing is untouched, so a building in front of a lamp still hides it —
   * renderOrder only sorts the transparent pass, it does not defeat the depth
   * buffer the opaque pass already wrote.
   */
  mesh.renderOrder = 9995
  mesh.visible = false
  mesh.castShadow = false
  mesh.receiveShadow = false
  mesh.frustumCulled = false
  // Parented to the bulb mesh itself: same space (merged statics are baked into
  // scene coordinates), same visibility — so `clearTown()` takes the pools away
  // with the lamps, and an asleep scene draws neither.
  bulbMesh.add(mesh)
  rigs.push({ mesh, material, heads: spots.map((s) => ({ x: s.x, y: s.y + HEAD_LIFT, z: s.z })) })
}

/**
 * (Re)build every pool of light in the world. Cheap — a few hundred vertices —
 * and only run when the world changes shape (a scene woke up, a save loaded, a
 * kid placed a lamp).
 */
export function rebuildLampGlow(): void {
  for (const r of rigs) {
    r.mesh.removeFromParent()
    r.mesh.geometry.dispose()
    r.material.dispose()
  }
  rigs.length = 0
  bulbCount = 0
  const bulbMat = flat('sunWarm')
  for (const id of world.ids()) {
    const node = world.get(id as SceneId)
    if (node === undefined) continue
    const found: THREE.Mesh[] = []
    node.root.traverse((o) => {
      const m = o as THREE.Mesh
      if (m.isMesh !== true) return
      if (m.name === 'time:lampGlow') return
      if (m.material !== bulbMat) return
      found.push(m)
    })
    for (const m of found) buildRigFor(m)
  }
}

/** Fade the pools with the night. Two writes; free when it is daylight. */
export function applyLampGlow(night: number): void {
  const on = night > 0.03
  for (const r of rigs) {
    if (r.mesh.visible !== on) r.mesh.visible = on
    if (on) r.material.opacity = Math.min(1, night)
  }
}

/** Diagnostics: how many lamps the town is lighting, in how many draw calls. */
export function lampGlowStats(): {
  bulbs: number
  meshes: number
  visible: number
  opacity: number
  spots: Array<{ x: number; y: number; z: number; scene: string; onScreen: boolean }>
  /** WHERE THE BULBS ARE, in world space — so a tool can photograph a lamp HEAD. */
  heads: Array<{ x: number; y: number; z: number; onScreen: boolean }>
} {
  const spots: Array<{ x: number; y: number; z: number; scene: string; onScreen: boolean }> = []
  const heads: Array<{ x: number; y: number; z: number; onScreen: boolean }> = []
  let visible = 0
  let opacity = 0
  for (const r of rigs) {
    let vis = true
    let o: THREE.Object3D | null = r.mesh
    while (o !== null) {
      if (!o.visible) vis = false
      o = o.parent
    }
    if (vis) visible++
    opacity = Math.max(opacity, r.material.opacity)
    const p = r.mesh.geometry.getAttribute('position')
    if (p !== undefined && p.count > 0) {
      const v = new THREE.Vector3(p.getX(0), p.getY(0), p.getZ(0))
      r.mesh.localToWorld(v)
      spots.push({ x: v.x, y: v.y, z: v.z, scene: r.mesh.parent?.name ?? '?', onScreen: vis })
    }
    for (const h of r.heads) {
      const v = new THREE.Vector3(h.x, h.y, h.z)
      r.mesh.localToWorld(v)
      heads.push({ x: v.x, y: v.y, z: v.z, onScreen: vis })
    }
  }
  return {
    bulbs: bulbCount,
    meshes: rigs.length,
    visible,
    opacity,
    spots: spots.slice(0, 4),
    heads: heads.slice(0, 12),
  }
}
